import { formatPrice, formatKm } from "@/lib/format";
import { ALL_FEATURE_KEYS, CAR_FEATURE_GROUPS, countSelectedFeatures } from "@/lib/carFeatures";

const dash = (v) => (v == null || v === "" ? "—" : v);

function fmtTuv(c) {
  const y = c.tuv_valid_year;
  const m = c.tuv_valid_month;
  if (!y) return null;
  return m ? `${y}. ${String(m).padStart(2, "0")}.` : String(y);
}

function fmtFirstReg(c) {
  if (!c.year) return null;
  if (c.year_month) return `${c.year}. ${String(c.year_month).padStart(2, "0")}.`;
  return String(c.year);
}

function fmtWarranty(c) {
  const parts = [];
  if (c.warranty_type) parts.push(c.warranty_type);
  if (c.warranty_until) parts.push(`ig ${c.warranty_until}`);
  if (c.warranty_km) parts.push(`${formatKm(c.warranty_km)}-ig`);
  return parts.length ? parts.join(" · ") : null;
}

function fmtBool(v) {
  if (v == null) return "—";
  return v ? "Igen" : "Nem";
}

/** @typedef {{ label: string, get: (c: object) => *, fmt?: (v: *) => string, better?: 'min'|'max'|null, always?: boolean }} CompareRow */

/** @param {CompareRow} row @param {object[]} cars */
export function rowHasAnyData(row, cars) {
  if (row.always) return true;
  return cars.some((c) => {
    const v = row.get(c);
    if (v == null || v === "" || v === false) return false;
    return true;
  });
}

/** @param {CompareRow} row @param {object[]} cars */
export function bestValueForRow(row, cars) {
  if (!row.better) return null;
  const vals = cars
    .map((c) => row.get(c))
    .filter((v) => v != null && v !== "" && typeof v === "number" && !Number.isNaN(v));
  if (!vals.length) return null;
  return row.better === "min" ? Math.min(...vals) : Math.max(...vals);
}

/**
 * @param {object[]} cars
 * @returns {{ title: string, rows: CompareRow[] }[]}
 */
export function buildCompareSections(cars) {
  /** @type {{ title: string, rows: CompareRow[] }[]} */
  const sections = [];

  const pushSection = (title, rows) => {
    const visible = rows.filter((r) => rowHasAnyData(r, cars));
    if (visible.length) sections.push({ title, rows: visible });
  };

  pushSection("Ár és futás", [
    { label: "Ár", get: (c) => c.price, fmt: formatPrice, better: "min", always: true },
    {
      label: "Akciós ár",
      get: (c) => c.sale_price,
      fmt: (v) => formatPrice(v),
      better: "min",
    },
    { label: "Futott km", get: (c) => c.mileage, fmt: formatKm, better: "min", always: true },
    { label: "Évjárat", get: (c) => c.year, fmt: (v) => dash(v), better: "max", always: true },
    { label: "Első forgalom", get: fmtFirstReg, fmt: (v) => dash(v) },
    { label: "Állapot", get: (c) => c.condition, fmt: (v) => dash(v) },
    { label: "Felszereltségi szint", get: (c) => c.trim_label, fmt: (v) => dash(v) },
    {
      label: "Felszereltség (pipák)",
      get: (c) => countSelectedFeatures(c.features),
      fmt: (v) => (v ? `${v} db` : "—"),
      better: "max",
    },
  ]);

  pushSection("Motor és hajtás", [
    { label: "Motor", get: (c) => c.engine, fmt: (v) => dash(v), always: true },
    {
      label: "Hengerűrtartalom",
      get: (c) => c.displacement_cm3,
      fmt: (v) => (v ? `${v} cm³` : "—"),
      better: null,
    },
    {
      label: "Teljesítmény",
      get: (c) => c.horsepower,
      fmt: (v) => (v ? `${v} LE` : "—"),
      better: "max",
    },
    {
      label: "Teljesítmény (kW)",
      get: (c) => c.power_kw,
      fmt: (v) => (v ? `${v} kW` : "—"),
      better: "max",
    },
    { label: "Üzemanyag", get: (c) => c.fuel, fmt: (v) => dash(v), always: true },
    { label: "Váltó", get: (c) => c.transmission, fmt: (v) => dash(v), always: true },
    { label: "Hajtás", get: (c) => c.drive, fmt: (v) => dash(v) },
    { label: "Euro norma", get: (c) => c.euro_class, fmt: (v) => dash(v) },
    {
      label: "Gyorsulás 0–100",
      get: (c) => c.acceleration,
      fmt: (v) => (v != null ? `${v} mp` : "—"),
      better: "min",
    },
  ]);

  pushSection("Méretek és tömeg", [
    { label: "Ajtók", get: (c) => c.doors, fmt: (v) => dash(v) },
    { label: "Ülések", get: (c) => c.seats, fmt: (v) => dash(v) },
    {
      label: "Saját tömeg",
      get: (c) => c.curb_weight_kg,
      fmt: (v) => (v ? `${v.toLocaleString("hu-HU")} kg` : "—"),
      better: "min",
    },
    {
      label: "Max. össztömeg",
      get: (c) => c.gross_weight_kg,
      fmt: (v) => (v ? `${v.toLocaleString("hu-HU")} kg` : "—"),
    },
    {
      label: "Csomagtér",
      get: (c) => c.trunk_liters,
      fmt: (v) => (v ? `${v} l` : "—"),
      better: "max",
    },
  ]);

  pushSection("Okmányok és garancia", [
    { label: "Műszaki érvényes", get: fmtTuv, fmt: (v) => dash(v) },
    { label: "Garancia", get: fmtWarranty, fmt: (v) => dash(v) },
    { label: "ÁFA visszaigénylhető", get: (c) => c.vat_reclaimable, fmt: fmtBool },
    { label: "Beszámítás lehetséges", get: (c) => c.trade_in_possible, fmt: fmtBool },
    { label: "Azonnal elvihető", get: (c) => c.immediate_available, fmt: fmtBool },
  ]);

  pushSection("Finanszírozás", [
    { label: "Hitellel / részlet", get: (c) => c.finance_available, fmt: fmtBool },
    {
      label: "Havi törlesztő",
      get: (c) => c.finance_monthly,
      fmt: (v) => (v ? `${formatPrice(v)} / hó` : "—"),
      better: "min",
    },
    {
      label: "Futamidő",
      get: (c) => c.finance_months,
      fmt: (v) => (v ? `${v} hó` : "—"),
    },
    {
      label: "Önerő",
      get: (c) => c.finance_down_payment,
      fmt: (v) => (v ? formatPrice(v) : "—"),
    },
    { label: "Lízing", get: (c) => c.lease_available, fmt: fmtBool },
  ]);

  const featureGroupIds = ["papers", "tech", "comfort", "safety", "multimedia", "extra"];
  const exteriorNonVisual = new Set([
    "equip_awd",
    "equip_tow_hitch",
    "equip_sport_package",
    "equip_non_smoker",
    "equip_one_owner",
    "equip_guaranteed_mileage",
    "equip_lady_owner",
  ]);

  for (const group of CAR_FEATURE_GROUPS.filter((g) => featureGroupIds.includes(g.id))) {
    /** @type {CompareRow[]} */
    const rows = [];
    for (const item of group.items) {
      const any = cars.some((c) => (c.features || []).includes(item.key));
      if (!any) continue;
      rows.push({
        label: item.label,
        get: (c) => (c.features || []).includes(item.key),
        fmt: (v) => (v ? "✓" : "—"),
        isFeature: true,
      });
    }
    pushSection(group.label, rows);
  }

  const exteriorGroup = CAR_FEATURE_GROUPS.find((g) => g.id === "exterior");
  if (exteriorGroup) {
    const rows = [];
    for (const item of exteriorGroup.items) {
      if (!exteriorNonVisual.has(item.key)) continue;
      const any = cars.some((c) => (c.features || []).includes(item.key));
      if (!any) continue;
      rows.push({
        label: item.label,
        get: (c) => (c.features || []).includes(item.key),
        fmt: (v) => (v ? "✓" : "—"),
        isFeature: true,
      });
    }
    pushSection("Történet és hajtás (pipák)", rows);
  }

  const customRows = [];
  const customLabels = new Set();
  for (const c of cars) {
    for (const f of c.features || []) {
      if (typeof f !== "string" || !f.trim()) continue;
      if (ALL_FEATURE_KEYS.has(f)) continue;
      if (f.startsWith("equip_") || f.startsWith("paper_")) continue;
      customLabels.add(f.trim());
    }
  }
  for (const label of [...customLabels].sort()) {
    customRows.push({
      label,
      get: (c) => (c.features || []).includes(label),
      fmt: (v) => (v ? "✓" : "—"),
      isFeature: true,
    });
  }
  pushSection("Egyéb felszereltség", customRows);

  return sections;
}
